import { useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Download, Upload, Database, FileJson } from "lucide-react";
import { toast } from "sonner";

// Onglet "Base de données" de la page Stock — permet d'EXPORTER les articles
// de la boutique active vers un fichier (nom, code, prix d'achat fournisseur,
// prix TTC, image, catégorie — la quantité en stock uniquement si la case est
// cochée), puis d'IMPORTER ce fichier sur une AUTRE boutique pour y recréer
// ces mêmes articles sans tout ressaisir à la main. Un article importé dont
// la quantité finale est 0 apparaît automatiquement dans "Produits hors
// stock" (aucune action particulière n'est nécessaire ici pour ça, voir
// backend/routers/stock_routes.py: import_database).
const IMPORT_MODES = [
  {
    value: "remplacer_tout",
    label: "Remplacer tout",
    help: "Les articles déjà présents (même code) sont entièrement écrasés, y compris leur quantité en stock si le fichier importé en contient.",
  },
  {
    value: "ne_rien_remplacer",
    label: "Ne rien remplacer",
    help: "Les articles déjà présents (même code) sont laissés tels quels et ignorés — seuls les articles réellement nouveaux sont ajoutés.",
  },
  {
    value: "remplacer_sans_stock",
    label: "Remplacer sans toucher au stock",
    help: "Les articles déjà présents (même code) sont mis à jour (nom, prix, image, catégorie...) mais leur quantité en stock actuelle n'est jamais modifiée.",
  },
];

export function DatabaseTab() {
  const { user } = useAuth();
  const canUse = hasPerm(user, "stock", "database");
  const fileInputRef = useRef(null);

  const [withQuantities, setWithQuantities] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [pendingArticles, setPendingArticles] = useState(null); // fichier chargé, en attente du choix de mode
  const [pendingFileName, setPendingFileName] = useState("");
  const [importMode, setImportMode] = useState("ne_rien_remplacer");
  const [importing, setImporting] = useState(false);

  if (!canUse) return null;

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/stock/database/export", { params: { with_quantities: withQuantities } });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `stock-export-${date}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${data.articles.length} article(s) exporté(s)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const articles = Array.isArray(parsed) ? parsed : parsed.articles;
      if (!Array.isArray(articles) || articles.length === 0) {
        toast.error("Fichier invalide : aucun article trouvé dedans");
        return;
      }
      setPendingArticles(articles);
      setPendingFileName(file.name);
      setImportMode("ne_rien_remplacer");
    } catch (err) {
      toast.error("Fichier invalide : ce n'est pas un export de base de données valide");
    }
  };

  const confirmImport = async () => {
    if (!pendingArticles) return;
    setImporting(true);
    try {
      const { data } = await api.post("/stock/database/import", { articles: pendingArticles, mode: importMode });
      toast.success(`Import terminé : ${data.created} ajouté(s), ${data.updated} remplacé(s), ${data.skipped} ignoré(s)`);
      setPendingArticles(null);
      setPendingFileName("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="stock-database-tab">
      <Card data-testid="stock-database-export-card">
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-2">
            <Download size={18} className="text-primary" />
            <h3 className="font-semibold text-base">Exporter</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Exporte les articles de cette boutique (nom, code, prix d'achat fournisseur, prix TTC, image, catégorie)
            dans un fichier que vous pourrez importer dans une autre boutique.
          </p>
          <label className="flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer hover:bg-muted/50">
            <Checkbox
              checked={withQuantities}
              onCheckedChange={(v) => setWithQuantities(Boolean(v))}
              data-testid="export-with-quantities-checkbox"
            />
            <span className="text-sm">
              <span className="font-medium">Exporter aussi les quantités en stock</span>
              <br />
              <span className="text-muted-foreground">Par défaut, seules les fiches articles sont exportées — pas les quantités.</span>
            </span>
          </label>
          <Button onClick={handleExport} disabled={exporting} className="w-full gap-2" data-testid="export-database-btn">
            <Download size={16} /> {exporting ? "Export en cours..." : "Exporter la base de données"}
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="stock-database-import-card">
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-2">
            <Upload size={18} className="text-primary" />
            <h3 className="font-semibold text-base">Importer</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Importe un fichier exporté depuis une autre boutique. Les articles déjà présents (même code) vous seront
            soumis avec plusieurs choix avant d'être modifiés.
          </p>
          <input ref={fileInputRef} type="file" accept="application/json,.json" className="hidden" onChange={handleFilePicked} data-testid="import-database-file-input" />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full gap-2" data-testid="import-database-pick-btn">
            <FileJson size={16} /> Choisir un fichier à importer
          </Button>
        </CardContent>
      </Card>

      <Dialog open={!!pendingArticles} onOpenChange={(open) => { if (!open) { setPendingArticles(null); setPendingFileName(""); } }}>
        <DialogContent data-testid="import-database-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database size={18} /> Importer « {pendingFileName} »
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {pendingArticles?.length} article(s) dans ce fichier. Si un article existe déjà dans cette boutique
              (même code), que voulez-vous faire ?
            </p>
            <RadioGroup value={importMode} onValueChange={setImportMode} className="space-y-2" data-testid="import-database-mode">
              {IMPORT_MODES.map((m) => (
                <label
                  key={m.value}
                  className="flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                >
                  <RadioGroupItem value={m.value} data-testid={`import-mode-${m.value}`} className="mt-0.5" />
                  <span className="text-sm">
                    <span className="font-medium">{m.label}</span>
                    <br />
                    <span className="text-muted-foreground">{m.help}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPendingArticles(null); setPendingFileName(""); }}>Annuler</Button>
            <Button onClick={confirmImport} disabled={importing} data-testid="confirm-import-database-btn">
              {importing ? "Import en cours..." : "Importer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
